import axios, { 
  AxiosInstance, 
  AxiosRequestConfig, 
  AxiosResponse, 
  AxiosError 
} from 'axios';
import { toast } from 'react-hot-toast';

import { ApiError } from '@/types/auth';
import { logger } from '@/utils/logger';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : 'https://prompthub-api.surfinn.kr/api';
const API_TIMEOUT = 30000; // 30 seconds

// Request/Response interceptor types
interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  skipErrorToast?: boolean;
  retryCount?: number;
}

interface ApiResponse<T = any> extends AxiosResponse<T> {
  config: ApiRequestConfig;
}

class ApiClient {
  private client: AxiosInstance;
  private readonly maxRetries = 3;
  private readonly retryDelay = 1000;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: ApiRequestConfig) => {
        // Add auth token if available and not skipped
        if (!config.skipAuth) {
          const token = localStorage.getItem('access_token');
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add request ID for tracing
        const requestId = this.generateRequestId();
        config.headers = config.headers || {};
        config.headers['x-request-id'] = requestId;

        // Log request in development
        if (import.meta.env.DEV) {
          logger.debug('API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            data: config.data,
            headers: config.headers,
          });
        }
        
        // Always log PATCH requests for debugging
        if (config.method === 'patch' && config.url?.includes('/prompts/')) {
          console.log('API PATCH Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            data: config.data,
            dataKeys: config.data ? Object.keys(config.data) : [],
            hasContent: !!config.data?.content,
            contentPreview: config.data?.content?.substring(0, 50)
          });
        }

        return config;
      },
      (error: AxiosError) => {
        logger.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: ApiResponse) => {
        // Log response in development
        if (import.meta.env.DEV) {
          logger.debug('API Response:', {
            status: response.status,
            url: response.config.url,
            data: response.data,
          });
        }

        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const config = error.config as ApiRequestConfig;
        
        // Log error
        logger.error('API Error:', {
          status: error.response?.status,
          url: config?.url,
          method: config?.method,
          data: error.response?.data,
          message: error.message,
        });

        // IMPORTANT: Skip all error handling for auth endpoints
        const authEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
        const isAuthEndpoint = authEndpoints.some(endpoint => config?.url?.includes(endpoint));
        
        if (isAuthEndpoint) {
          // For auth endpoints, just reject the error without any processing
          // This prevents page refresh, redirects, and toast messages
          return Promise.reject(error);
        }

        // Handle different error types for non-auth endpoints
        if (error.response) {
          await this.handleResponseError(error, config);
        } else if (error.request) {
          await this.handleRequestError(error, config);
        } else {
          await this.handleGeneralError(error, config);
        }

        return Promise.reject(error);
      }
    );
  }

  private async handleResponseError(
    error: AxiosError<ApiError>,
    config: ApiRequestConfig
  ): Promise<void> {
    const status = error.response?.status;
    const data = error.response?.data;

    switch (status) {
      case 401:
        // Auth endpoints are already filtered out in the interceptor
        await this.handleUnauthorizedError(error, config);
        break;
      
      case 403:
        if (!config.skipErrorToast) {
          toast.error(data?.error || '권한이 없습니다.');
        }
        break;
      
      case 404:
        if (!config.skipErrorToast) {
          toast.error(data?.error || '요청하신 리소스를 찾을 수 없습니다.');
        }
        break;
      
      case 422:
        if (!config.skipErrorToast) {
          this.handleValidationError(data);
        }
        break;
      
      case 429:
        if (!config.skipErrorToast) {
          toast.error('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
        }
        break;
      
      case 500:
      case 502:
      case 503:
      case 504:
        await this.handleServerError(error, config);
        break;
      
      default:
        if (!config.skipErrorToast) {
          toast.error(data?.error || '알 수 없는 오류가 발생했습니다.');
        }
    }
  }

  private async handleRequestError(
    error: AxiosError,
    config: ApiRequestConfig
  ): Promise<void> {
    // Network error or timeout
    if (error.code === 'ECONNABORTED') {
      if (!config.skipErrorToast) {
        toast.error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
      }
    } else {
      // Try to retry on network errors
      if (this.shouldRetry(config)) {
        return this.retryRequest(config);
      }

      if (!config.skipErrorToast) {
        toast.error('네트워크 오류가 발생했습니다. 연결을 확인해주세요.');
      }
    }
  }

  private async handleGeneralError(
    error: AxiosError,
    config: ApiRequestConfig
  ): Promise<void> {
    if (!config.skipErrorToast) {
      toast.error('예상치 못한 오류가 발생했습니다.');
    }
  }

  private async handleUnauthorizedError(
    error: AxiosError<ApiError>,
    config: ApiRequestConfig
  ): Promise<void> {
    // This function is only called for non-auth endpoints
    const refreshToken = this.getCookie('refresh_token');
    
    // Try to refresh token for non-auth endpoints
    if (refreshToken && !config.url?.includes('/auth/refresh-token')) {
      try {
        const response = await this.client.post('/auth/refresh-token', 
          { refreshToken },
          { skipAuth: true }
        );
        
        const { tokens } = response.data;
        localStorage.setItem('access_token', tokens.accessToken);
        
        // Retry original request
        if (config.headers) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }
        
        return this.client.request(config);
      } catch (refreshError) {
        logger.error('Token refresh failed:', refreshError);
        this.handleAuthFailure();
        if (!config.skipErrorToast) {
          toast.error('인증이 만료되었습니다. 다시 로그인해주세요.');
        }
      }
    } else {
      // No refresh token available
      this.handleAuthFailure();
      if (!config.skipErrorToast) {
        toast.error('인증이 만료되었습니다. 다시 로그인해주세요.');
      }
    }
  }

  private async handleServerError(
    error: AxiosError<ApiError>,
    config: ApiRequestConfig
  ): Promise<void> {
    // Retry server errors
    if (this.shouldRetry(config)) {
      return this.retryRequest(config);
    }

    if (!config.skipErrorToast) {
      toast.error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    }
  }

  private handleValidationError(data: ApiError | undefined): void {
    if (data?.details && Array.isArray(data.details)) {
      // Show first validation error
      const firstError = data.details[0];
      toast.error(firstError?.message || '입력값을 확인해주세요.');
    } else {
      toast.error(data?.error || '입력값을 확인해주세요.');
    }
  }

  private handleAuthFailure(): void {
    // Clear tokens
    localStorage.removeItem('access_token');
    this.deleteCookie('refresh_token');
    
    // Redirect to login if not already there
    if (!window.location.pathname.includes('/auth')) {
      window.location.href = '/auth/login';
    }
  }

  private shouldRetry(config: ApiRequestConfig): boolean {
    const retryCount = config.retryCount || 0;
    return retryCount < this.maxRetries;
  }

  private async retryRequest(config: ApiRequestConfig): Promise<any> {
    const retryCount = (config.retryCount || 0) + 1;
    const delay = this.retryDelay * Math.pow(2, retryCount - 1); // Exponential backoff

    await new Promise(resolve => setTimeout(resolve, delay));

    config.retryCount = retryCount;
    return this.client.request(config);
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  // Public methods
  async get<T = any>(url: string, config?: ApiRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: ApiRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  // Upload with progress
  async upload<T = any>(
    url: string,
    data: FormData,
    onUploadProgress?: (progressEvent: any) => void,
    config?: ApiRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  }

  // Set default header
  setDefaultHeader(key: string, value: string): void {
    this.client.defaults.headers.common[key] = value;
  }

  // Remove default header
  removeDefaultHeader(key: string): void {
    delete this.client.defaults.headers.common[key];
  }

  // Get axios instance for advanced usage
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export types
export type { ApiRequestConfig, ApiResponse };